"use client";

import React from "react";
import { FaRegCopy } from "react-icons/fa";

/**
 * Reusable CSS clip-path promo ticket.
 * Parent controls width via `className` (e.g. w-full, w-[350px]); height follows aspect-ratio.
 *
 * Overlay layout (Figma 258×81.14): vertical "Promo" on green stub;
 * white body = large discount | promo-code stack | stacked % OFF.
 */
export default function PromoTicket({
  className = "w-full",
  promoDiscountPercentage,
  promoCode,
  copied,
  onCopy,
}) {
  const handleCopyClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onCopy?.(e);
  };

  return (
    <div className={className}>
      <div className="promo-tag-wrapper" role="img" aria-label="Promo">
        <div className="promo-tag-outline promo-tag-clip" aria-hidden="true" />
        <div className="promo-tag-outer promo-tag-clip" aria-hidden="true" />
        <div className="promo-tag-pin" aria-hidden="true" />

        <div className="absolute inset-0 z-[1] flex overflow-hidden">
          {/* Green stub — vertical "Promo" (Figma ~18% / rotate -90deg) */}
          <div className="relative flex w-[18.221%] shrink-0 items-center justify-center">
            <span
              className="absolute left-[33%] top-1/2 font-bold leading-none text-black text-[clamp(0.55rem,6.3cqi,1.05rem)]"
              style={{ transform: "translate(-50%, -50%) rotate(-90deg)" }}
            >
              Promo
            </span>
          </div>

          {/* White body — starts after pin/perforation (~27% from left overall) */}
          <div className="flex min-w-0 flex-1 items-center gap-[clamp(0.1rem,0.8cqi,0.25rem)] pl-[8.8%] pr-[5%]">
            {/* Large discount number — floor low enough for ~130px tickets */}
            <span className="min-w-0 shrink font-bold leading-none tracking-tight text-[#000000] text-[clamp(0.75rem,19.3cqi,42px)]">
              {promoDiscountPercentage ?? 0}
            </span>

            {/* Promo code stack */}
            <div className="flex min-w-0 flex-1 flex-col items-center gap-[clamp(0.05rem,1.4cqi,0.25rem)] leading-none overflow-hidden">
              <p className="font-semibold text-black text-[clamp(0.35rem,3.5cqi,0.7rem)] truncate max-w-full">
                Promo Code
              </p>
              <p className="min-w-0 max-w-full truncate whitespace-nowrap font-extrabold underline text-black text-[clamp(0.45rem,5.5cqi,13px)]">
                {promoCode}
              </p>
              <button
                type="button"
                className="relative z-10 flex min-w-0 max-w-full items-center gap-[clamp(0.1rem,1.4cqi,0.25rem)] border-none bg-transparent p-0 cursor-pointer"
                onClick={handleCopyClick}
                style={{
                  userSelect: "none",
                  touchAction: "manipulation",
                  WebkitTapHighlightColor: "transparent",
                }}
                aria-label="Copy promo code"
              >
                <span className="font-normal leading-none text-black text-[clamp(0.35rem,3.5cqi,0.7rem)] truncate">
                  {copied ? "Copied" : "Copy Code"}
                </span>
                <FaRegCopy className="shrink-0 text-black text-[clamp(0.35rem,3.5cqi,0.8rem)]" />
              </button>
            </div>

            {/* Stacked % OFF */}
            <div className="flex min-w-0 shrink flex-col items-center justify-center leading-[0.9] text-[#000000]">
              <span className="font-bold text-[clamp(0.4rem,7.2cqi,1.2rem)]">
                %
              </span>
              <span className="font-bold text-[clamp(0.4rem,7.2cqi,1.2rem)]">
                OFF
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
